/**
 * 사업계획서용 rhwp 에디터 호스트
 * — 셀프호스트 스튜디오(field-api-v1) 우선, 없으면 @rhwp/core export→set→reload 우회
 * — fieldClick 은 스튜디오가 window.parent 로 보내는 rhwp-event 를 구독
 */
import {
  applyFieldValuesToHwp,
  extractFieldValuesFromHwp,
} from 'budget/bizPlanHwpFieldApply';

export const SELF_STUDIO_URL = '/rhwp-studio/';

function studioUrlWithChrome(chrome) {
  if (chrome !== 'minimal') return SELF_STUDIO_URL;
  return `${SELF_STUDIO_URL}?chrome=minimal`;
}

function parseMaybeJson(v) {
  if (v == null) return v;
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
}

function asFieldList(raw) {
  const parsed = parseMaybeJson(raw);
  return Array.isArray(parsed) ? parsed : [];
}

function valueFromGet(raw) {
  const detail = parseMaybeJson(raw);
  if (detail?.value != null) return String(detail.value);
  if (typeof raw === 'string' && !raw.trim().startsWith('{')) return raw;
  return '';
}

async function studioAvailable() {
  try {
    const resp = await fetch(`${SELF_STUDIO_URL}index.html`, { method: 'GET', cache: 'no-store' });
    if (!resp.ok) return false;
    const text = await resp.text();
    // CRA 폴백(앱 index.html) 이 아니라 스튜디오 페이지인지 확인
    return /rhwp-studio|rhwpStudio|studio-root/i.test(text);
  } catch {
    return false;
  }
}

/**
 * @param {HTMLElement} hostEl
 * @param {{ onFieldClick?: (payload: { name: string, fieldId?: number, guide?: string }) => void, chrome?: 'full'|'minimal' }} [options]
 */
export async function createBizPlanEditor(hostEl, options = {}) {
  const { createEditor } = await import('@rhwp/editor');
  const useSelf = await studioAvailable();
  const chrome = options.chrome === 'minimal' ? 'minimal' : 'full';
  const editor = await createEditor(hostEl, {
    width: '100%',
    height: '100%',
    ...(useSelf ? { studioUrl: studioUrlWithChrome(chrome) } : {}),
  });

  const transport = editor._transport;
  const supportsFieldApi = !!(transport?.supports?.('field-api-v1'));
  const fieldClickListeners = new Set();
  if (typeof options.onFieldClick === 'function') {
    fieldClickListeners.add(options.onFieldClick);
  }

  const onWindowEvent = (event) => {
    const data = event?.data;
    if (!data || data.type !== 'rhwp-event' || data.event !== 'fieldClick') return;
    const iframe = editor.element;
    if (event.source && iframe?.contentWindow && event.source !== iframe.contentWindow) {
      return;
    }
    const payload = data.payload || {};
    fieldClickListeners.forEach((fn) => {
      try {
        fn(payload);
      } catch (e) {
        console.warn('[rhwpEditorHost] fieldClick handler failed', e);
      }
    });
  };
  window.addEventListener('message', onWindowEvent);

  const request = (method, params) => editor._request(method, params);

  async function exportHwp() {
    return editor.exportHwp();
  }

  async function loadFile(bytes, fileName, loadOptions) {
    return editor.loadFile(bytes, fileName, loadOptions);
  }

  async function getFieldList() {
    if (supportsFieldApi) {
      try {
        return asFieldList(await request('getFieldList'));
      } catch (e) {
        console.warn('[rhwpEditorHost] getFieldList studio failed, fallback', e);
      }
    }
    const bytes = await exportHwp();
    const fields = await extractFieldValuesFromHwp(bytes);
    return Object.entries(fields).map(([name, value]) => ({ name, value }));
  }

  async function getFieldText(name) {
    if (!name) return '';
    if (supportsFieldApi) {
      try {
        return valueFromGet(await request('getFieldText', { name }));
      } catch (e) {
        console.warn('[rhwpEditorHost] getFieldText studio failed, fallback', e);
      }
    }
    const list = await getFieldList();
    const found = list.find((f) => f.name === name);
    return found?.value != null ? String(found.value) : '';
  }

  async function putFieldText(name, value, fileName = 'biz-plan.hwp') {
    if (!name) return { ok: false };
    const text = value == null ? '' : String(value);
    if (supportsFieldApi) {
      try {
        const raw = await request('putFieldText', { name, value: text });
        return parseMaybeJson(raw) || { ok: true };
      } catch (e) {
        console.warn('[rhwpEditorHost] putFieldText studio failed, fallback', e);
      }
    }
    const exported = await exportHwp();
    const { bytes, results } = await applyFieldValuesToHwp(exported, { [name]: text });
    await loadFile(bytes, fileName, { suppressDialogs: true, skipUnsavedGuard: true });
    const one = results?.[0];
    return { ok: one?.ok !== false, detail: one?.detail };
  }

  async function putFieldMap(fieldValues, fileName = 'biz-plan.hwp') {
    const entries = Object.entries(fieldValues || {}).filter(([k]) => k);
    if (!entries.length) return { ok: true, results: [] };
    if (supportsFieldApi) {
      const results = [];
      for (const [name, value] of entries) {
        try {
          const raw = await request('putFieldText', {
            name,
            value: value == null ? '' : String(value),
          });
          results.push({ name, ok: parseMaybeJson(raw)?.ok !== false, detail: parseMaybeJson(raw) });
        } catch (e) {
          results.push({ name, ok: false, detail: String(e?.message || e) });
        }
      }
      return { ok: results.every((r) => r.ok), results };
    }
    const exported = await exportHwp();
    const applied = await applyFieldValuesToHwp(exported, Object.fromEntries(entries));
    await loadFile(applied.bytes, fileName, { suppressDialogs: true, skipUnsavedGuard: true });
    return { ok: applied.results.every((r) => r.ok), results: applied.results, bytes: applied.bytes };
  }

  async function extractFields(fieldNames = []) {
    if (supportsFieldApi) {
      try {
        const list = asFieldList(await request('getFieldList'));
        const out = {};
        list.forEach((f) => {
          if (f?.name) out[f.name] = f.value != null ? String(f.value) : '';
        });
        if (fieldNames.length) {
          for (const name of fieldNames) {
            if (out[name]) continue;
            out[name] = await getFieldText(name);
          }
        }
        return out;
      } catch (e) {
        console.warn('[rhwpEditorHost] extractFields studio failed, fallback', e);
      }
    }
    const bytes = await exportHwp();
    return extractFieldValuesFromHwp(bytes, fieldNames);
  }

  async function notifySaved(fileName) {
    try {
      if (transport?.supports?.('notify-saved-v1')) {
        return await editor.notifySaved(fileName);
      }
    } catch (e) {
      console.warn('[rhwpEditorHost] notifySaved skipped', e);
    }
    return { ok: true, wasDirty: false };
  }

  return {
    editor,
    supportsFieldApi,
    studioUrl: useSelf ? SELF_STUDIO_URL : 'cdn',
    element: editor.element,
    loadFile,
    exportHwp,
    pageCount: () => editor.pageCount(),
    getFieldList,
    getFieldText,
    putFieldText,
    putFieldMap,
    extractFields,
    notifySaved,
    onFieldClick(fn) {
      if (typeof fn === 'function') fieldClickListeners.add(fn);
      return () => fieldClickListeners.delete(fn);
    },
    /** 메뉴·툴바·눈금자 접기 (셀프호스트 스튜디오만) */
    setChrome(mode) {
      const next = mode === 'minimal' ? 'minimal' : 'full';
      try {
        editor.element?.contentWindow?.postMessage({ type: 'rhwp-chrome', mode: next }, '*');
      } catch (e) {
        console.warn('[rhwpEditorHost] setChrome failed', e);
      }
    },
    destroy() {
      window.removeEventListener('message', onWindowEvent);
      fieldClickListeners.clear();
      try {
        editor.destroy?.();
      } catch {
        /* ignore */
      }
    },
  };
}

/** 양식 문서 공통 호스트 (결의서·사업계획서 등) */
export const createRhwpFormEditor = createBizPlanEditor;
